import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

/** `celebrate` (coral) is for the child's moment or the single most important action on a screen — at most one per screen. */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'celebrate';
export type ButtonSize = 'md' | 'lg';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'bg-lagoon-600 text-white shadow-lagoon hover:bg-lagoon-700',
  secondary: 'bg-white text-lagoon-600 border-[1.5px] border-sage-300 hover:border-lagoon-500 hover:bg-mist',
  tertiary: 'bg-transparent text-lagoon-600 underline-offset-4 hover:underline',
  danger: 'bg-alert-soft text-alert-ink',
  celebrate: 'bg-coral-500 text-[#3B1A0F] shadow-coral hover:bg-[#EE7C62]',
};

export type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
  full?: boolean;
  as?: 'button' | 'a';
  children?: ReactNode;
  className?: string;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> &
  Partial<Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel' | 'download'>> & {
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  };

const Spinner = () => (
  <span
    aria-hidden="true"
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
);

export const Button = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  full = false,
  as = 'button',
  className = '',
  children,
  disabled,
  type,
  ...rest
}: ButtonProps) => {
  // `as` is a fixed, literal-narrowed prop; rendering it dynamically needs an
  // escape hatch since <button> and <a> don't share an attribute surface.
  const Tag = as as unknown as 'button';
  const isDisabled = disabled || loading;
  const sharedProps = {
    className: cx(
      'inline-flex min-h-btn items-center justify-center gap-2 rounded-md text-label font-extrabold transition duration-fade ease-fade disabled:opacity-50 disabled:pointer-events-none',
      VARIANT_CLASS[variant],
      size === 'lg' ? 'px-6' : 'px-5',
      full && 'w-full',
      className,
    ),
    'aria-busy': loading ? true : undefined,
  };

  if (as === 'a') {
    return (
      <a
        {...sharedProps}
        aria-disabled={isDisabled ? true : undefined}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(rest as any)}
      >
        {loading ? <Spinner /> : icon}
        {children}
      </a>
    );
  }

  return (
    <Tag {...sharedProps} type={type ?? 'button'} disabled={isDisabled} {...rest}>
      {loading ? <Spinner /> : icon}
      {children}
    </Tag>
  );
};
